import { z } from "zod";

// eslint-disable-next-line no-restricted-syntax
export enum MappingLevel {
  GLOBAL = "GLOBAL",
  STATE = "STATE",
  COUNTY = "COUNTY",
  MSA = "MSA",
  HCF = "HCF",
}

export function requiresLevelConfirmation(level: MappingLevel): boolean {
  return level === MappingLevel.GLOBAL || level === MappingLevel.STATE;
}

// eslint-disable-next-line no-restricted-syntax
export enum Domain {
  FLEX = "FLEX",
  HIRING = "HIRING",
}

export const REQUIREMENT_MAPPING_RESOURCE = "requirement-mappings";
export const REQUIREMENT_RESOURCE = "requirements";

export const requirementMappingAttributesSchema = z.object({
  level: z.nativeEnum(MappingLevel),
  qualifications: z.array(z.string()),
  workplaceTypes: z.array(z.string()),
  requiredBy: z.array(z.string()),
  requiredForBooking: z.boolean(),
  domain: z.nativeEnum(Domain),
  isOrientationRequirement: z.boolean(),
});

export type RequirementMappingAttributes = z.infer<typeof requirementMappingAttributesSchema>;

export const requirementMappingSchema = z.object({
  attributes: requirementMappingAttributesSchema,
  id: z.string(),
  type: z.literal(REQUIREMENT_MAPPING_RESOURCE),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        id: z.string(),
        type: z.literal(REQUIREMENT_RESOURCE),
      }),
    }),
  }),
});

export type RequirementMapping = z.infer<typeof requirementMappingSchema>;

export const requirementMappingResponseSchema = z.object({
  data: requirementMappingSchema,
});

export type RequirementMappingResponse = z.infer<typeof requirementMappingResponseSchema>;

export const createRequirementMappingSchema = z.object({
  data: z.object({
    type: z.literal(REQUIREMENT_MAPPING_RESOURCE),
    attributes: requirementMappingAttributesSchema,
    relationships: z.object({
      requirement: z.object({
        data: z.object({
          id: z.string(),
          type: z.literal(REQUIREMENT_RESOURCE),
        }),
      }),
    }),
  }),
});

export type CreateRequirementMappingDto = z.infer<typeof createRequirementMappingSchema>;

export const updateRequirementMappingSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(REQUIREMENT_MAPPING_RESOURCE),
    attributes: requirementMappingAttributesSchema,
    relationships: z.object({
      requirement: z.object({
        data: z.object({
          id: z.string(),
          type: z.literal(REQUIREMENT_RESOURCE),
        }),
      }),
    }),
  }),
});

export type UpdateRequirementMappingDto = z.infer<typeof updateRequirementMappingSchema>;

export const requirementMappingFormSchema = z
  .object({
    requirementId: z.string().min(1, "Requirement is required"),
    level: z.nativeEnum(MappingLevel),
    domain: z.nativeEnum(Domain),
    qualifications: z.array(z.string()),
    allWorkplaceTypes: z.boolean(),
    workplaceTypes: z.array(z.string()),
    requiredBy: z.array(z.string()).optional(),
    requiredByHCFs: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        })
      )
      .optional(),
    requiredForBooking: z.boolean(),
    isOrientationRequirement: z.boolean(),
    levelConfirmation: z.string().optional(),
  })
  .refine((data) => data.level !== MappingLevel.HCF || (data.requiredByHCFs?.length ?? 0) > 0, {
    message: "At least one required by HCF is required",
    path: ["requiredByHCFs"],
  })
  .refine(
    (data) =>
      ![MappingLevel.STATE, MappingLevel.COUNTY, MappingLevel.MSA].includes(data.level) ||
      (data.requiredBy?.length ?? 0) > 0,
    {
      message: "At least one entity is required",
      path: ["requiredBy"],
    }
  )
  .refine(
    (data) =>
      data.level === MappingLevel.HCF ||
      data.allWorkplaceTypes ||
      (data.workplaceTypes?.length ?? 0) > 0,
    {
      message: "At least one workplace type is required",
      path: ["workplaceTypes"],
    }
  )
  .refine((data) => (data.qualifications?.length ?? 0) > 0, {
    message: "At least one qualification is required",
    path: ["qualifications"],
  })
  .refine(
    (data) => {
      if (requiresLevelConfirmation(data.level)) {
        // Validate that the level confirmation text is the exact level name
        return data.level.toUpperCase() === data.levelConfirmation?.trim().toUpperCase();
      }

      return true;
    },
    {
      message: "Please type the level name exactly to confirm",
      path: ["levelConfirmation"],
    }
  );

export type RequirementMappingFormValues = z.infer<typeof requirementMappingFormSchema>;
